import { buildSentryRelease, resolveSentryEnvironment } from '@gde-mit/angular-core';

import * as Sentry from '@sentry/angular';
import { makeFetchTransport } from '@sentry/browser';
import type { Integration } from '@sentry/core';

import type { KtSentryConfig } from './kt-sentry-config';
import { buildBeforeSend, buildBeforeSendLog, buildBeforeSendTransaction } from './sentry-before-send';
import { createCircuitBreakerTransport } from './sentry-circuit-breaker-transport';

/**
 * Initialises Sentry for KT with a transport circuit breaker for ad-blocked environments.
 *
 * @param config The Config value.
 */
export function initKtSentry(config: KtSentryConfig): void {
  const environment = resolveSentryEnvironment(config.environment, config.buildInfo.localUsername);
  const release = buildSentryRelease(config.buildInfo.version, config.buildInfo.commitHash);
  const enablePiiFiltering = config.enablePiiFiltering ?? false;

  const integrations: Integration[] = [Sentry.browserApiErrorsIntegration()];

  if (config.tracesSampleRate !== undefined) {
    integrations.push(Sentry.browserTracingIntegration());
  }

  const transportFailureThreshold = config.transportFailureThreshold ?? 2;
  const transport =
    transportFailureThreshold > 0
      ? createCircuitBreakerTransport(makeFetchTransport, {
          failureThreshold: transportFailureThreshold,
          onCircuitOpen: () => Sentry.close(),
        })
      : makeFetchTransport;

  Sentry.init({
    dsn: config.dsn,
    environment,
    release,
    integrations,
    tracesSampleRate: config.tracesSampleRate,
    tunnel: config.tunnel,
    transport,
    beforeSend: buildBeforeSend(enablePiiFiltering),
    beforeSendTransaction: buildBeforeSendTransaction(),
    beforeSendLog: buildBeforeSendLog(),
    // No `dataCollection` here: any value for it makes @sentry/core resolve its permissive defaults
    // (user info, cookies, headers, HTTP bodies) instead of following `sendDefaultPii`.
    sendDefaultPii: false,
    enableLogs: true,
  });

  sendStartupLog(environment, release);
}

/**
 *
 * @param environment The Environment value.
 * @param release The Release value.
 */
function sendStartupLog(environment: string, release: string): void {
  Sentry.logger.info('Application started', {
    environment,
    release,
    timestamp: new Date().toISOString(),
    userAgent: typeof navigator === 'undefined' ? 'unknown' : navigator.userAgent,
  });
}
